/**
 * State Data Module
 *
 * Purpose: Contains all Australian state/territory data for FSS requirements
 * Data Source: fss_requirements_by_state.csv, fss_requirements_by_state_updated.csv,
 *              fss_state_considerations.csv, fss_recertification_requirements.csv
 * Usage: StateSelector component, calculation engine
 */

const stateData = {
  NSW: {
    code: 'NSW',
    name: 'New South Wales',
    fssRequired: true,
    fssRequiredDetails: 'Yes (Non-Licensed aged care, others)',
    fssRequiredNote: 'Only SITX units accepted; licensed aged care need written program; HLTSS00061 recommended',
    validityYears: 5,
    appointmentDeadlineDays: 30,
    displayRequired: true,
    displayDetails: 'Must be prominently displayed',
    councilNotification: false,
    councilNotificationDetails: null,
    standardDeadline: '2024-12-08',
    standardDeadlineExtended: true,
    standard322aApplicable: true,
    governingBody: {
      name: 'NSW Food Authority',
      website: 'https://www.foodauthority.nsw.gov.au/',
      contactInfo: 'NSW Food Authority'
    },
    penalties: {
      individual: 'Up to $330',
      company: 'Up to $660'
    },
    specialRequirements: [
      'NSW Food Authority specific training',
      'Must display certificate',
      '4 key focus areas must be covered'
    ],
    focusAreas: [
      'Allergen management',
      'Cleaning/sanitising',
      'Safe egg handling',
      'Food Act offences'
    ],
    auditRequirements: 'NSW Food Authority inspections',
    recertification: {
      required: true,
      method: 'NSW Food Safety Supervisor Recertification Program',
      gracePeriodDays: 90,
      durationHours: '1-2',
      fullCourseAlternative: true,
      costRange: '$100-200'
    }
  },

  VIC: {
    code: 'VIC',
    name: 'Victoria',
    fssRequired: false,
    fssRequiredDetails: 'No (Not legally mandated)',
    fssRequiredNote: 'Councils/auditors expect documented FSS training',
    fssRecommended: true,
    validityYears: 5,
    appointmentDeadlineDays: 30,
    displayRequired: false,
    displayDetails: null,
    councilNotification: false,
    councilNotificationDetails: null,
    standardDeadline: '2028-12-08',
    standardDeadlineExtended: true,
    standard322aApplicable: true,
    governingBody: {
      name: 'Department of Health Victoria',
      website: 'https://www.health.vic.gov.au/',
      contactInfo: 'Department of Health Victoria'
    },
    penalties: {
      individual: 'Varies',
      company: 'Varies'
    },
    specialRequirements: [
      'Bi-annual audits for aged care',
      'Standard 3.2.2A compliance from Dec 2023',
      'Extension to 2028 for some sectors'
    ],
    focusAreas: [
      'Standard 3.2.2A compliance'
    ],
    auditRequirements: 'Bi-annual for aged care',
    recertification: {
      required: true,
      method: 'Refresher course or full re-certification',
      gracePeriodDays: 30,
      durationHours: '2-4',
      fullCourseAlternative: true,
      costRange: '$90-200'
    }
  },

  QLD: {
    code: 'QLD',
    name: 'Queensland',
    fssRequired: true,
    fssRequiredDetails: 'Yes (High-risk businesses)',
    fssRequiredNote: 'Mandatory for high-risk food businesses',
    validityYears: 5,
    appointmentDeadlineDays: 30,
    displayRequired: false,
    displayDetails: 'Available on request',
    councilNotification: true,
    councilNotificationDetails: 'Must notify local council within 14 days of appointment',
    standardDeadline: '2023-12-08',
    standardDeadlineExtended: false,
    standard322aApplicable: true,
    governingBody: {
      name: 'Queensland Health',
      website: 'https://www.health.qld.gov.au/',
      contactInfo: 'Queensland Health'
    },
    penalties: {
      individual: 'Varies',
      company: 'Varies'
    },
    specialRequirements: [
      'Must notify local council within 14 days of appointment',
      'Third-party auditing required'
    ],
    focusAreas: [
      'Standard 3.2.2A compliance'
    ],
    auditRequirements: 'Third-party audits for Category 1',
    recertification: {
      required: true,
      method: 'Refresher course or full re-certification',
      gracePeriodDays: 30,
      durationHours: '2-4',
      fullCourseAlternative: true,
      costRange: '$90-200'
    }
  },

  WA: {
    code: 'WA',
    name: 'Western Australia',
    fssRequired: true,
    fssRequiredDetails: 'Yes',
    fssRequiredNote: 'Mandatory under Standard 3.2.2A',
    validityYears: 5,
    appointmentDeadlineDays: 30,
    displayRequired: false,
    displayDetails: null,
    councilNotification: false,
    councilNotificationDetails: null,
    standardDeadline: '2023-12-08',
    standardDeadlineExtended: false,
    standard322aApplicable: true,
    governingBody: {
      name: 'WA Health',
      website: 'https://www.health.wa.gov.au/',
      contactInfo: 'WA Health'
    },
    penalties: {
      individual: 'Varies',
      company: 'Varies'
    },
    specialRequirements: [
      'Risk-based inspection schedules',
      'Standard 3.2.2A compliance required'
    ],
    focusAreas: [
      'Standard 3.2.2A compliance'
    ],
    auditRequirements: 'Risk-based inspections',
    recertification: {
      required: true,
      method: 'Refresher course or full re-certification',
      gracePeriodDays: 30,
      durationHours: '2-4',
      fullCourseAlternative: true,
      costRange: '$90-200'
    }
  },

  SA: {
    code: 'SA',
    name: 'South Australia',
    fssRequired: true,
    fssRequiredDetails: 'Yes (aged care)',
    fssRequiredNote: 'Standard 3.2.2A now requires one in aged care',
    validityYears: 5,
    appointmentDeadlineDays: 30,
    displayRequired: false,
    displayDetails: null,
    councilNotification: false,
    councilNotificationDetails: null,
    standardDeadline: '2023-12-08',
    standardDeadlineExtended: false,
    standard322aApplicable: true,
    governingBody: {
      name: 'SA Health',
      website: 'https://www.sahealth.sa.gov.au/',
      contactInfo: 'SA Health'
    },
    penalties: {
      individual: 'Varies',
      company: 'Varies'
    },
    specialRequirements: [
      'Food Safety Program notification and approval required under Food Act 2001'
    ],
    focusAreas: [
      'Food Safety Program requirements'
    ],
    auditRequirements: 'Program approval required',
    recertification: {
      required: true,
      method: 'Refresher course or full re-certification',
      gracePeriodDays: 30,
      durationHours: '2-4',
      fullCourseAlternative: true,
      costRange: '$90-200'
    }
  },

  TAS: {
    code: 'TAS',
    name: 'Tasmania',
    fssRequired: true,
    fssRequiredDetails: 'Yes',
    fssRequiredNote: 'Required for all high-risk food operations',
    validityYears: 5,
    appointmentDeadlineDays: 30,
    displayRequired: false,
    displayDetails: null,
    councilNotification: false,
    councilNotificationDetails: null,
    standardDeadline: '2023-12-08',
    standardDeadlineExtended: false,
    standard322aApplicable: true,
    governingBody: {
      name: 'Department of Health Tasmania',
      website: 'https://www.health.tas.gov.au/',
      contactInfo: 'Department of Health Tasmania'
    },
    penalties: {
      individual: 'Varies',
      company: 'Varies'
    },
    specialRequirements: [
      'Standard 3.2.2A compliance for high-risk food businesses'
    ],
    focusAreas: [
      'High-risk food focus'
    ],
    auditRequirements: 'Standard inspections',
    recertification: {
      required: true,
      method: 'Refresher course or full re-certification',
      gracePeriodDays: 30,
      durationHours: '2-4',
      fullCourseAlternative: true,
      costRange: '$90-200'
    }
  },

  NT: {
    code: 'NT',
    name: 'Northern Territory',
    fssRequired: true,
    fssRequiredDetails: 'Yes (aged care)',
    fssRequiredNote: 'Required for aged care sites',
    validityYears: 5,
    appointmentDeadlineDays: 30,
    displayRequired: false,
    displayDetails: null,
    councilNotification: false,
    councilNotificationDetails: null,
    standardDeadline: '2023-12-08',
    standardDeadlineExtended: false,
    standard322aApplicable: true,
    governingBody: {
      name: 'NT Department of Health',
      website: 'https://health.nt.gov.au/',
      contactInfo: 'NT Department of Health'
    },
    penalties: {
      individual: 'Varies',
      company: 'Varies'
    },
    specialRequirements: [
      'Standard 3.2.2A compliance required for all facilities'
    ],
    focusAreas: [
      'Standard 3.2.2A compliance'
    ],
    auditRequirements: 'Standard inspections',
    recertification: {
      required: true,
      method: 'Refresher course or full re-certification',
      gracePeriodDays: 30,
      durationHours: '2-4',
      fullCourseAlternative: true,
      costRange: '$90-200'
    }
  },

  ACT: {
    code: 'ACT',
    name: 'Australian Capital Territory',
    fssRequired: true,
    fssRequiredDetails: 'Yes',
    fssRequiredNote: 'Required for high-risk operations',
    validityYears: 5,
    appointmentDeadlineDays: 30,
    displayRequired: false,
    displayDetails: null,
    councilNotification: false,
    councilNotificationDetails: null,
    standardDeadline: '2023-12-08',
    standardDeadlineExtended: false,
    standard322aApplicable: true,
    governingBody: {
      name: 'ACT Health Protection Service',
      website: 'https://www.health.act.gov.au/',
      contactInfo: 'ACT Health Protection Service'
    },
    penalties: {
      individual: 'Varies',
      company: 'Varies'
    },
    specialRequirements: [
      'FSS required for high-risk food operations',
      '5-year renewal recommended'
    ],
    focusAreas: [
      'High-risk operations focus'
    ],
    auditRequirements: 'Standard inspections',
    recertification: {
      required: true,
      method: 'Refresher course or full re-certification',
      gracePeriodDays: 30,
      durationHours: '2-4',
      fullCourseAlternative: true,
      costRange: '$90-200'
    }
  }
};

export default stateData;